/**
 * Experience vocabulary — single source of truth for how the four domain
 * concepts (persona / scenario / walkthrough / finding) are *named* and
 * *colored* across the dashboard.
 *
 * Every page that shows a verdict word, a severity dot, a criticality
 * label, or a walkthrough status imports from this file. Do not redefine
 * these maps locally — if a surface needs a different phrasing, add a
 * variant here and let both callers pick.
 *
 * Conventions:
 *   - `*Label` returns a short display string (for kickers and pills)
 *   - `*Sentence` returns a full sentence (for editorial body)
 *   - `*Tone` returns a semantic tone key; `toneTextClass` / `toneDotClass`
 *     map tone → Tailwind class. This keeps color decisions out of callers.
 */

import type { FindingType, Severity } from "@/components/finding-badges";
import {
  findingTypeColorMap,
  severityTextColorMap,
  SEVERITY_ORDER,
} from "@/components/finding-badges";

// ────────────────────────────────────────────────────────────────
// Verdict
// ────────────────────────────────────────────────────────────────

export type VerdictRating = "yes" | "partially" | "no" | "not_applicable";

/** Short word for kickers and pills — "Yes / Partial / No / N/A" */
const verdictLabelMap: Record<VerdictRating, string> = {
  yes: "Yes",
  partially: "Partial",
  no: "No",
  not_applicable: "N/A",
};

/** Full sentence for editorial body copy */
const verdictSentenceMap: Record<VerdictRating, string> = {
  yes: "Would return voluntarily",
  partially: "Uncertain whether they'd return",
  no: "Would not return",
  not_applicable: "Verdict not applicable",
};

export function verdictLabel(rating: VerdictRating): string {
  return verdictLabelMap[rating];
}

export function verdictSentence(rating: VerdictRating): string {
  return verdictSentenceMap[rating];
}

/**
 * Worst-case rating from a walkthrough's verdict array.
 * Priority: no > partially > yes > not_applicable.
 */
export function getPrimaryVerdict(
  verdict: Array<{ rating: VerdictRating | string }> | null | undefined,
): VerdictRating | null {
  if (!verdict || !Array.isArray(verdict) || verdict.length === 0) return null;
  const priority: VerdictRating[] = [
    "no",
    "partially",
    "yes",
    "not_applicable",
  ];
  for (const r of priority) {
    if (verdict.some((v) => v.rating === r)) return r;
  }
  const fallback = verdict[0]?.rating;
  if (fallback && fallback in verdictLabelMap) {
    return fallback as VerdictRating;
  }
  return null;
}

// ────────────────────────────────────────────────────────────────
// Walkthrough status
// ────────────────────────────────────────────────────────────────

export type WalkthroughStatus =
  | "completed"
  | "running"
  | "pending"
  | "failed"
  | string;

const walkthroughStatusLabelMap: Record<string, string> = {
  completed: "Completed",
  running: "In progress",
  pending: "Queued",
  failed: "Failed",
  blocked: "Blocked",
};

export function walkthroughStatusLabel(status: WalkthroughStatus): string {
  return walkthroughStatusLabelMap[status] ?? status;
}

// ────────────────────────────────────────────────────────────────
// Tone — the single axis that combines verdict + status into color
// ────────────────────────────────────────────────────────────────

export type Tone = "helps" | "critical" | "important" | "muted" | "running";

/**
 * Collapse (status, verdict) into a single tone key used by every
 * walkthrough surface — status dot, verdict word color, card accent.
 */
export function composeWalkthroughTone(
  status: WalkthroughStatus,
  rating: VerdictRating | null,
): Tone {
  if (status === "failed") return "critical";
  if (status === "running") return "running";
  if (status === "pending") return "muted";
  if (!rating) return "muted";
  if (rating === "yes") return "helps";
  if (rating === "no") return "critical";
  if (rating === "partially") return "important";
  return "muted";
}

export function toneTextClass(tone: Tone): string {
  switch (tone) {
    case "helps":
      return "text-finding-helps";
    case "critical":
      return "text-severity-critical";
    case "important":
      return "text-severity-important";
    case "running":
      return "text-primary";
    default:
      return "text-muted-foreground";
  }
}

export function toneDotClass(tone: Tone): string {
  switch (tone) {
    case "helps":
      return "bg-mark-helps";
    case "critical":
      return "bg-mark-critical";
    case "important":
      return "bg-mark-important";
    case "running":
      return "bg-primary";
    default:
      return "bg-muted-foreground/40";
  }
}

export function toneBorderClass(tone: Tone): string {
  switch (tone) {
    case "helps":
      return "border-finding-helps";
    case "critical":
      return "border-severity-critical";
    case "important":
      return "border-severity-important";
    case "running":
      return "border-primary";
    default:
      return "border-border";
  }
}

// ────────────────────────────────────────────────────────────────
// Scenario criticality
// ────────────────────────────────────────────────────────────────

export type TaskCriticality =
  | "critical_path"
  | "important"
  | "optional"
  // legacy shorthand values still present in some records
  | "critical"
  | "low"
  | string;

const criticalityLabelMap: Record<string, string> = {
  critical_path: "Critical path",
  important: "Important",
  optional: "Optional",
  critical: "Critical",
  low: "Optional",
};

export function criticalityLabel(criticality: TaskCriticality): string {
  return criticalityLabelMap[criticality] ?? criticality;
}

export function criticalityTone(criticality: TaskCriticality): Tone {
  if (criticality === "critical_path" || criticality === "critical") {
    return "critical";
  }
  if (criticality === "important") return "important";
  return "muted";
}

// ────────────────────────────────────────────────────────────────
// Finding — labels, type colors, kicker-style display
// ────────────────────────────────────────────────────────────────

/** Short kicker label for a finding type — "Issue / Gap / Strength / …" */
const findingTypeKickerLabelMap: Record<FindingType, string> = {
  hinders: "Issue",
  missing: "Gap",
  helps: "Strength",
  irrelevant: "Off-topic",
  incorrect: "Incorrect",
};

/** Natural-language label — "Helps / Hinders / Missing / …" */
const findingTypeLabelMap: Record<FindingType, string> = {
  helps: "Helps",
  hinders: "Hinders",
  missing: "Missing",
  irrelevant: "Irrelevant",
  incorrect: "Incorrect",
};

/** Color used in kickers and other non-badge contexts */
const findingTypeKickerColorMap: Record<FindingType, string> = {
  hinders: "text-severity-critical",
  missing: "text-severity-important",
  helps: "text-finding-helps",
  irrelevant: "text-muted-foreground",
  incorrect: "text-severity-important",
};

/**
 * All finding-type helpers accept `FindingType | string` because upstream
 * data (API responses, canonical findings) often widens the type. Unknown
 * values fall back to a neutral label/color.
 */
export function findingTypeKickerLabel(type: FindingType | string): string {
  return findingTypeKickerLabelMap[type as FindingType] ?? String(type);
}

export function findingTypeLabel(type: FindingType | string): string {
  return findingTypeLabelMap[type as FindingType] ?? String(type);
}

export function findingTypeKickerColor(type: FindingType | string): string {
  return (
    findingTypeKickerColorMap[type as FindingType] ?? "text-muted-foreground"
  );
}

/**
 * Dedicated finding-* tokens (text-finding-helps, text-finding-hinders,
 * text-finding-missing). Use in body copy where the finding *is* the
 * subject; use `findingTypeKickerColor` for kicker labels that share the
 * severity scale.
 *
 * `incorrect` is grouped with `hinders` (both render in the hinders red
 * rather than the neutral severity-minor color `finding-badges` assigns).
 * An incorrect finding is a negative outcome — it reads wrong visually
 * as muted-gray.
 */
export function findingTypeColor(type: FindingType | string): string {
  if (type === "incorrect") return "text-finding-hinders";
  return (
    findingTypeColorMap[type as FindingType]?.color ?? "text-muted-foreground"
  );
}

/**
 * Background-color token for polarity dots (the small circle that
 * anchors a finding row). Mirrors `findingTypeColor` but returns a
 * `bg-*` class.
 */
const findingTypeDotClassMap: Record<FindingType, string> = {
  helps: "bg-mark-helps",
  hinders: "bg-mark-critical",
  missing: "bg-mark-important",
  irrelevant: "bg-muted-foreground/40",
  incorrect: "bg-mark-critical",
};

export function findingTypeDotClass(type: FindingType | string): string {
  return (
    findingTypeDotClassMap[type as FindingType] ?? "bg-muted-foreground/40"
  );
}

/**
 * Border-color token for finding rows — the Semgrep-style left accent
 * bar. Polarity-colored so a user can scan 20 findings and see at a
 * glance how many are strengths vs issues vs gaps.
 */
const findingTypeBorderClassMap: Record<FindingType, string> = {
  helps: "border-finding-helps",
  hinders: "border-finding-hinders",
  missing: "border-finding-missing",
  irrelevant: "border-border",
  incorrect: "border-finding-hinders",
};

export function findingTypeBorderClass(type: FindingType | string): string {
  return findingTypeBorderClassMap[type as FindingType] ?? "border-border";
}

const severityLabelMap: Record<Severity, string> = {
  critical: "Critical",
  major: "Major",
  minor: "Minor",
  nit: "Nit",
};

export function severityLabel(severity: Severity): string {
  return severityLabelMap[severity];
}

// ────────────────────────────────────────────────────────────────
// Finding sort — shared across every surface that lists findings
// ────────────────────────────────────────────────────────────────

/**
 * Type precedence for sorting — issues/gaps first, strengths last.
 * Leading with minor strengths buries the things that need attention,
 * so every finding list uses this order.
 */
const findingTypeRank: Record<FindingType, number> = {
  hinders: 0,
  incorrect: 1,
  missing: 2,
  irrelevant: 3,
  helps: 4,
};

const severityRank: Record<Severity, number> = {
  critical: 0,
  major: 1,
  minor: 2,
  nit: 3,
};

/**
 * Comparator for sorting findings by criticality. Primary key is type
 * (negatives first, strengths last). Secondary is severity within type.
 * Accepts a minimal shape so canonical, raw, and walkthrough findings
 * all share one sort.
 */
export function compareFindingsByCriticality(
  a: {
    type?: FindingType | string | null;
    severity?: Severity | string | null;
  },
  b: {
    type?: FindingType | string | null;
    severity?: Severity | string | null;
  },
): number {
  const ta = findingTypeRank[a.type as FindingType] ?? 99;
  const tb = findingTypeRank[b.type as FindingType] ?? 99;
  if (ta !== tb) return ta - tb;
  const sa = severityRank[(a.severity as Severity) ?? "nit"] ?? 99;
  const sb = severityRank[(b.severity as Severity) ?? "nit"] ?? 99;
  return sa - sb;
}

// ────────────────────────────────────────────────────────────────
// Re-exports — one import for consumers
// ────────────────────────────────────────────────────────────────

export { findingTypeColorMap, SEVERITY_ORDER, severityTextColorMap };
export type { FindingType, Severity };
